<template lang="pug">
TransitionExpand
  form.prose.dark_prose-invert.p-4.relative.w-full.max-w-full(
    @submit="submit"
    ref="form"
    v-if="toValue(open)"
  )
    button.btn.btn-icon.opacity-50.aspect-square.flex.items-center.justify-center.font-bold.text-xl.w-8.h-8.absolute.top-4.right-4(
      @click="close"
      type="button"
    ) &times;

    h3 Save your work

    p.opacity-70.max-w-full.w-96 Please write a short message describing your changes.

    UnsavedChangesDiff.mt-4.mb-6(:draft="draft")

    textarea.text-lg.w-full.p-2.ring-2.ring-gray-200.transition.rounded.focus_ring-4.focus_ring-sky-400(
      :disabled="submitStatus === 'pending' || submitStatus === 'success'"
      ref="textarea"
      v-model="message"
    )
    p.mt-1.text-sm.text-gray-500 Press Enter to submit, Shift+Enter for new line.

    p.flex.flex-row-reverse.gap-6 
      button.btn.btn-primary.px-4.py-2.btn-hover_bg-opacity-90.active_bg-opacity-80(
        :class="{ 'bg-primary-600 text-white': !disabled, 'bg-gray-200 text-gray-900': disabled }"
        :disabled="disabled"
        type="submit"
      ) Save
      p.text-xs.text-right &nbsp; {{ submitMessage }}
</template>

<script setup lang="ts">
import TransitionExpand from './TransitionExpand.vue';
import { ref, computed, watchEffect, toValue } from 'vue';
import { commitDraft } from '../lib/callModContentAuthors';
import { useEventListener } from '@vueuse/core';
import type { Ref } from 'vue';
import { updateRecentlySaved } from '../store/recentlySaved';
import UnsavedChangesDiff from './UnsavedChangesDiff.vue';

const props = defineProps<{
  open: boolean | Ref<boolean>;
  draft: CourseDraft | null;
  baseCourse?: CourseDraft | null;
}>();
const emit = defineEmits<{
  close: [];
  success: [validation?: SaveValidationResult];
}>();
const textarea = ref<HTMLTextAreaElement>();
const form = ref<HTMLFormElement>();
const message = ref('');
const clearOnClose = ref(false);
const submitStatus = ref<'idle' | 'pending' | 'success' | 'error'>('idle');

const disabled = computed(
  () =>
    !props.draft ||
    message.value.length === 0 ||
    submitStatus.value === 'pending' ||
    submitStatus.value === 'success'
);

watchEffect(() => {
  if (toValue(props.open)) {
    submitStatus.value = 'idle';
    setTimeout(() => textarea.value?.focus(), 200);
  }
});

useEventListener(form, 'keydown', (e: KeyboardEvent) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    submit(e);
  }
});

const submitMessage = computed(() => {
  switch (submitStatus.value) {
    case 'pending':
      return 'Committing...';
    case 'success':
      return 'Success!';
    case 'error':
      return 'Something went wrong.';
    default:
      return ' ';
  }
});

const close = () => {
  if (clearOnClose.value) {
    message.value = '';
    clearOnClose.value = false;
  }

  emit('close');
};

const submit = async (e: Event) => {
  e.preventDefault();
  submitStatus.value = 'pending';

  if (!props.draft) return;

  // The save endpoint runs a validation phase (e.g. adding missing IDs) and,
  // when it rewrites the YAML, returns the validated YAML so the editor can
  // refresh in place.
  let validation: SaveValidationResult | undefined;

  try {
    const [, result] = await Promise.all([
      new Promise(resolve => setTimeout(resolve, 1000)),
      commitDraft({
        draft: props.draft,
        commitMessage: message.value,
        baseCourse: props.baseCourse
      })
    ]);

    validation = result?.data?.data?.results as unknown as
      | SaveValidationResult
      | undefined;

    submitStatus.value = 'success';
  } catch (e) {
    submitStatus.value = 'error';
  }

  clearOnClose.value = true;
  emit('success', validation);

  updateRecentlySaved(props.draft);
};
</script>
